import { CompanyDto, CompaniesListDto } from "Api/Api";
import {
  PaginationParams,
  PrimaryDataTable,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import * as React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { DetailColumn } from "Components/Shared/PrimaryDataTable/DetailColumn";
import { Dictionary } from "lodash";
import { FilterProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableFilter";
import { OrderByProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableOrderBy";
import { getRoleResource } from "Components/Companies/CompanyDetail/CompanyDetailRole";

type Props = {
  companies: CompaniesListDto;
  onPageChange: (page: number, rows: number) => void;
  isLoading: boolean;
  resetTablePage?: boolean;
  filter: Dictionary<FilterProps>;
  orderBy: OrderByProps<CompanyDto>;
  paginationParams?: PaginationParams | null;
};

const StyledTableWrapper = styled.div`
  .postal-code {
    width: 50px;
  }

  .date-updated {
    width: 90px;
  }

  .name {
    max-width: 520px;
  }

  .street {
    width: 200px;
  }

  .action-buttons {
    display: flex;
    align-items: center;
  }

  thead {
    .filter-popup {
      .selectbox {
        .ibis-MuiInputBase-root {
          min-width: 190px;
        }
      }
    }
  }
`;

export const CompaniesTable: React.FunctionComponent<Props> = props => {
  const {
    companies,
    onPageChange,
    isLoading,
    resetTablePage,
    filter,
    orderBy,
    paginationParams,
  } = props;
  const history = useHistory();
  const { t } = useResource();

  const onRowClick = (company: CompanyDto) => {
    const companyID = company.companyID;

    history.push(
      `/${t(Resources.Routes.Companies)}/${t(
        Resources.Routes.CompanyDetail,
      )}/${companyID}`,
    );
  };

  const getCell = (id: TableIDType<CompanyDto>, company: CompanyDto) => {
    switch (id) {
      case "name":
        return <div className={"name"}>{company.name}</div>;

      case "street":
        return (
          <div className={"street"}>
            {company.street} {company.streetNo}
          </div>
        );

      case "postalCode":
        return <div className={"postal-code"}>{company.postalCode}</div>;

      case "city":
        return <div>{company.city}</div>;

      case "isCustomer":
        return <div>{getRoleResource(company)}</div>;

      case "dateUpdated":
        return (
          <div className={"date-updated"}>
            {formatDate(company.dateUpdated)}
          </div>
        );

      case "isActive":
        return <div>{company.isActive ? "Ano" : "Ne"}</div>;

      case "detail":
        return (
          <DetailColumn
            url={`/${t(Resources.Routes.Companies)}/${t(
              Resources.Routes.CompanyDetail,
            )}/${company.companyID}`}
          />
        );

      default:
        return <div>-</div>;
    }
  };

  return (
    <StyledTableWrapper>
      <PrimaryDataTable
        isLoading={isLoading}
        rows={
          companies.companies?.map(company => ({
            data: company,
            rowEntityID: "companyID",
          })) ?? []
        }
        columns={[
          {
            id: "name",
            label: t(Resources.Companies.List.Name),
            width: "35%",
            filter: filter["name"],
            orderBy: orderBy,
          },
          {
            id: "street",
            label: t(Resources.Companies.List.Street),
            width: "14%",
            filter: filter["street"],
            orderBy: orderBy,
          },
          {
            id: "postalCode",
            label: t(Resources.Companies.List.PostalCode),
            width: "10%",
            filter: filter["postalCode"],
            orderBy: orderBy,
          },
          {
            id: "city",
            label: t(Resources.Companies.List.City),
            width: "14%",
            filter: filter["city"],
            orderBy: orderBy,
          },
          {
            id: "isCustomer",
            label: t(Resources.Companies.List.Role),
            width: "14%",
            filter: filter["role"],
          },
          {
            id: "isActive",
            label: "Aktivní",
            width: "5%",
            filter: filter["isActive"],
            orderBy: orderBy,
          },
          {
            id: "dateUpdated",
            label: t(Resources.Companies.List.LastUpdate),
            width: "1%",
            orderBy: orderBy,
          },
          {
            id: "detail",
          },
        ]}
        getCell={getCell}
        onPageChange={onPageChange}
        totalCount={companies.count}
        onRowClick={onRowClick}
        getHeaderCell={column => <div>{column.label}</div>}
        resetTablePage={resetTablePage}
        paginationParams={paginationParams}
      />
    </StyledTableWrapper>
  );
};
